import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    createManagementEfficiencyRatio,
    deleteManagementEfficiencyRatio,
} from '@/modules/Report/components/BusinessPerformance/services'
import {
    ManagementEfficiencyRatio,
    ManagementEfficiencyRatioCreateParams,
} from '@/modules/Report/components/BusinessPerformance/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import * as yup from 'yup'

export interface RatioFormModalProps extends ModalProps {
    refetch?: () => void
    editData?: ManagementEfficiencyRatio | null
    mode: 'create' | 'edit'
}

interface FormData {
    report_year: number
    report_month: number
    roa: number
    roe: number
    roic: number
    roce: number
    notes?: string
}

const currentYear = dayjs().year()

const RatioFormModal: React.FC<RatioFormModalProps> = ({
    open,
    onClose,
    refetch,
    editData,
    mode,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                report_month: yup
                    .string()
                    .required(t('required'))
                    .matches(/^\d{4}-\d{2}$/, t('invalid_month_format')),
                roa: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                roe: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                roic: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                roce: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                notes: yup.string(),
            }),
        [t]
    )

    // Default values
    const defaultValues: FormData = useMemo(
        () => ({
            report_year: dayjs().year(),
            report_month: dayjs().month() + 1,
            roa: 0,
            roe: 0,
            roic: 0,
            roce: 0,
            notes: '',
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when dialog opens/closes or editData changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && editData) {
                reset({
                    report_year: parseInt(editData.report_month.split('-')[0]),
                    report_month: parseInt(editData.report_month.split('-')[1]),
                    roa: editData.roa,
                    roe: editData.roe,
                    roic: editData.roic,
                    roce: editData.roce,
                    notes: editData.notes || '',
                })
            } else {
                reset(defaultValues)
            }
        }
    }, [open, editData, mode, reset, defaultValues])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            const payload: ManagementEfficiencyRatioCreateParams = {
                report_month: `${values.report_year}-${values.report_month}`,
                roa: values.roa,
                roe: values.roe,
                roic: values.roic,
                roce: values.roce,
                notes: values.notes || '',
            }

            // Upsert (create or update based on report_month)
            await createManagementEfficiencyRatio(payload)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_MANAGEMENT_EFFICIENCY_RATIOS],
            })

            // Show success message
            toast.success(
                mode === 'edit' ? t('update-success') : t('create-success')
            )

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to save management efficiency ratio:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!editData) return

        const confirmed = window.confirm(
            t('confirm_delete_management_efficiency_ratio')
        )
        if (!confirmed) return

        try {
            setIsDeleting(true)

            await deleteManagementEfficiencyRatio({
                report_month: editData.report_month,
            })

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_MANAGEMENT_EFFICIENCY_RATIOS],
            })

            // Show success message
            toast.success(t('delete-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error(
                'Failed to delete management efficiency ratio:',
                error
            )
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                mode === 'edit'
                    ? t('edit_management_efficiency_ratio')
                    : t('create_management_efficiency_ratio')
            }
            maxWidth="md"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                {/* Report Year */}
                <FormItem
                    control={control}
                    name="report_year"
                    type="select"
                    label={t('report_year')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('year')} ${currentYear - index}`,
                        value: currentYear - index,
                    }))}
                    allowClear={false}
                    disabled={mode === 'edit'}
                />
                {/* Report Month */}
                <FormItem
                    control={control}
                    name="report_month"
                    type="select"
                    label={t('report_month')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('month')} ${index + 1}`,
                        value: index + 1,
                    }))}
                    allowClear={false}
                    placeholder="YYYY-MM (VD: 2024-12)"
                    disabled={mode === 'edit'}
                />

                {/* ROA */}
                <FormItem
                    control={control}
                    name="roa"
                    type="number"
                    label={t('roa')}
                    placeholder={t('enter_roa')}
                />

                {/* ROE */}
                <FormItem
                    control={control}
                    name="roe"
                    type="number"
                    label={t('roe')}
                    placeholder={t('enter_roe')}
                />

                {/* ROIC */}
                <FormItem
                    control={control}
                    name="roic"
                    type="number"
                    label={t('roic')}
                    placeholder={t('enter_roic')}
                />

                {/* ROCE */}
                <FormItem
                    control={control}
                    name="roce"
                    type="number"
                    label={t('roce')}
                    placeholder={t('enter_roce')}
                />

                {/* Notes */}
                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                    placeholder={t('enter_notes')}
                />

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                    <div>
                        {mode === 'edit' && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={isSubmitting || isDeleting}
                                loading={isDeleting}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting || isDeleting}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting || isDeleting}
                            loading={isSubmitting}
                        >
                            {mode === 'edit' ? t('update') : t('create')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default RatioFormModal
